// src/pages/health/HealthSurvey.js
import AuthCard from "../../../components/auth/AuthCard";
import BottomNav from "../../../components/common/BottomNav";
import { useLoadingGate } from "../../../hooks/useLoadingGate";
import { useEffect, useMemo, useState } from "react";
import { HealthSurveyCard } from "../../../components/cards/HealthSurveyCard";
import { useDeleteParq, useParq, useUpsertParq } from "../../../hooks/useHealthSurvey";
import SurveyModal from "./components/SurveyModal";
import { useNavigate } from "react-router-dom";

const QUESTIONS = [
    "의사로부터 심장 질환이 있으며, 의사가 추천하는 신체 활동만 해야 한다는 말을 들은 적이 있습니까?",
    "신체 활동을 할 때 가슴에 통증을 느낍니까?",
    "지난 한 달 동안, 신체 활동을 하지 않을 때도 가슴에 통증을 느낀 적이 있습니까?",
    "어지럼증으로 인해 균형을 잃거나, 의식을 잃은 적이 있습니까?",
    "신체 활동량의 변화로 인해 악화될 수 있는 뼈나 관절 문제(예: 허리, 무릎, 고관절)가 있습니까?",
    "현재 의사로부터 혈압이나 심장 질환으로 인해 약(예: 이뇨제)을 처방 받고 있습니까?",
    "신체 활동을 해서는 안 되는 다른 의학적 이유가 있습니까?",
];

export default function HealthSurvey() {
    const navigate = useNavigate();
    const ready = useLoadingGate();

    const { data, isLoading } = useParq();
    const upsert = useUpsertParq();
    const del = useDeleteParq();

    const [answers, setAnswers] = useState(() => QUESTIONS.map(() => null));
    const [note, setNote] = useState("");

    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    useEffect(() => {
        if (!data) return;
        const next = [data.q1, data.q2, data.q3, data.q4, data.q5, data.q6, data.q7].map(
            (b) => (b === true ? "yes" : "no")
        );
        setAnswers(next);
        setNote(data.note ?? "");
    }, [data]);

    const setAnswer = (idx, v) =>
        setAnswers((prev) => prev.map((x, i) => (i === idx ? v : x)));

    const allAnswered = useMemo(
        () => answers.every((v) => v === "yes" || v === "no"),
        [answers]
    );
    const yesCount = useMemo(
        () => answers.filter((a) => a === "yes").length,
        [answers]
    );

    const payload = useMemo(() => {
        const [q1, q2, q3, q4, q5, q6, q7] = answers.map((v) => v === "yes");
        return { q1, q2, q3, q4, q5, q6, q7, note: note?.trim() || null };
    }, [answers, note]);

    const handleSave = async () => {
        await upsert.mutateAsync(payload);
        setIsSaveModalOpen(false);
        handleBack();
    };

    const handleDeleteConfirm = async () => {
        await del.mutateAsync();
        // 초기화
        setAnswers(QUESTIONS.map(() => null));
        setNote("");
        setIsDeleteModalOpen(false);
        handleBack();
    };

    const handleClose = () => {
        if (window.history?.state?.idx > 0) {
            navigate(-1);
        } else {
            navigate("/dashboard", { replace: true });
        }
    };

    const handleBack = () => {
        if (window.history?.state?.idx > 0) {
            navigate(-1);
        } else {
            navigate("/dashboard", { replace: true });
        }
    };

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full mx-auto"
                    onClose={() => { handleClose() }}
                    title={"건강 설문"}
                >
                    <div className="flex flex-col flex-1 pt-5 pb-28 gap-3">
                        {(isLoading ? QUESTIONS : QUESTIONS).map((q, i) => (
                            <HealthSurveyCard
                                key={i}
                                number={i + 1}
                                text={q}
                                value={answers[i]}
                                onChange={(v) => setAnswer(i, v)}
                                disabled={upsert.isPending || del.isPending}
                            />
                        ))}

                        {/* 비고(선택) */}
                        <div className="mt-2">
                            <label className="block mb-2 text-sm font-semibold text-gray-700">
                                비고(선택)
                            </label>
                            <textarea
                                className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#5F9BFF]/50"
                                rows={3}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="추가로 기록할 내용을 입력하세요."
                            />
                        </div>

                        {/* 액션 버튼 */}
                        <div className="mt-1 flex flex-col gap-3">
                            {data && (
                                <button
                                    type="button"
                                    className="rounded-lg bg-[#9F9F9F] px-4 py-2.5 text-base md:text-lg font-semibold text-white"
                                    onClick={() => setIsDeleteModalOpen(true)}
                                    disabled={del.isPending || upsert.isPending}
                                >
                                    건강 설문 삭제
                                </button>
                            )}

                            <button
                                type="button"
                                className="rounded-lg bg-[#5F9BFF] px-4 py-2.5 text-base md:text-lg font-semibold text-white disabled:opacity-50"
                                onClick={() => {
                                    setIsSaveModalOpen(true);
                                }}
                                disabled={!allAnswered || upsert.isPending}
                            >
                                건강 설문 저장
                            </button>
                        </div>
                    </div>
                </AuthCard>
            )}

            <BottomNav className="h-16" />

            <SurveyModal
                isOpen={isSaveModalOpen}
                onClose={() => setIsSaveModalOpen(false)}
                onConfirm={handleSave}
                title="건강 설문 저장"
                message="건강 설문을 다음과 같이 저장합니다."
                confirmText={upsert.isPending ? "저장" : "저장"}
                confirmDisabled={upsert.isPending}
            >
                <div className="bg-gray-100 border border-gray-200 rounded-lg py-2 md:py-3">
                    {`7문항 중 ${yesCount}문항 '예' 응답`}
                </div>
            </SurveyModal>

            <SurveyModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="건강 설문 삭제"
                message="정말 삭제하시겠어요? 이 작업은 되돌릴 수 없습니다."
                confirmText={del.isPending ? "삭제" : "삭제"}
                cancelText="취소"
                danger
                confirmDisabled={del.isPending}
            >
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg py-2 md:py-3">
                    {`현재 응답 요약: '예' ${yesCount} / 7`}
                </div>
            </SurveyModal>
        </main>
    );
}
